// frontend/src/pages/DatabaseUpload.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import NavBar from '../components/NavBar';

const API_URL = API_BASE_URL;

const DatabaseUpload = () => {
  const [file, setFile] = useState(null);
  const [dbName, setDbName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.sql')) {
      setFile(selectedFile);
      setError('');
      // Set a default name based on the file name (without extension)
      if (!dbName) {
        const fileName = selectedFile.name.replace('.sql', '');
        setDbName(fileName);
      }
    } else {
      setError('Please select a valid SQL file');
      setFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a SQL file to upload');
      return;
    }

    if (!dbName.trim()) {
      setError('Please enter a database name');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('sql_file', file);
    formData.append('db_name', dbName.trim());
    formData.append('db_type', 'sqlite');

    try {
      const response = await axios.post(`${API_URL}/database/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      if (response.data.connection_id) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/chat/${response.data.connection_id}`);
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload SQL file');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.sql')) {
      setFile(droppedFile);
      setError('');
      if (!dbName) {
        const fileName = droppedFile.name.replace('.sql', '');
        setDbName(fileName);
      }
    } else {
      setError('Please drop a valid SQL file');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900">
      <NavBar />
      
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
            Upload SQL File
          </h1>
          <p className="text-gray-400">Upload your SQL dump file to create an instant database</p>
        </div>

        <div className="card-gradient p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                SQL File
              </label>
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  file 
                    ? 'border-violet-500 bg-violet-900/20' 
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                {file ? (
                  <div className="space-y-2">
                    <div className="text-4xl">📄</div>
                    <p className="text-violet-300 font-medium">{file.name}</p>
                    <p className="text-gray-400 text-sm">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-6xl text-gray-500">📤</div>
                    <div>
                      <p className="text-gray-300 mb-2">Drag and drop your SQL file here</p>
                      <p className="text-gray-500 text-sm mb-4">or</p>
                      <label className="btn-primary cursor-pointer inline-block">
                        Browse Files
                        <input
                          type="file"
                          accept=".sql"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    <p className="text-gray-500 text-xs">
                      Supports: .sql files up to 100MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Database Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Database Name
              </label>
              <input
                type="text"
                value={dbName}
                onChange={(e) => setDbName(e.target.value)}
                required
                placeholder="my_database"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
              />
              <p className="text-gray-500 text-xs mt-1">
                This will be the name of your temporary database
              </p>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-violet-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg text-green-300 text-sm">
                SQL file uploaded successfully! Creating database and redirecting...
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={uploading || !file || !dbName.trim()}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
                  uploading || !file || !dbName.trim()
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {uploading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </span>
                ) : (
                  'Upload & Create Database'
                )}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 border-2 border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
        
        {/* Help Section */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <h3 className="font-medium text-gray-400 mb-2">How it works:</h3>
          <div className="space-y-1">
            <p>1. Upload your SQL dump file (.sql format)</p>
            <p>2. We create a temporary SQLite database</p>
            <p>3. Import your data and make it queryable instantly</p>
            <p>4. Start chatting with your data using natural language</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseUpload;